import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faArrowLeft,
  faInfoCircle,
  faComments,
} from "@fortawesome/free-solid-svg-icons"; // Import from free-solid-svg-icons
import {
  getMotMinistreById,
  createMotMinistre,
  updateMotMinistre,
  getMotMinistre,
} from "../../services/api";
import { MotMinistre } from "../../types/types";
import { toast } from "react-toastify";
import AdminLayout from "./AdminLayout";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};

const MotMinistreForm: React.FC = () => {
  const { language } = useLanguage();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingRecord, setHasExistingRecord] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState<number | null>(null);

  // Text states
  const [arabicText, setArabicText] = useState("");
  const [frenchText, setFrenchText] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MotMinistre>();

  useEffect(() => {
    // Check for existing records
    const checkExistingRecord = async () => {
      try {
        const response = await getMotMinistre();
        if (response.data.length > 0) {
          setHasExistingRecord(true);
          setExistingRecordId(response.data[0].id || null);

          if (!isEditMode) {
            toast.info("يوجد كلمة وزير بالفعل. سيتم نقلك إلى صفحة التعديل.");
            navigate(`/admin/mot-ministre/edit/${response.data[0].id}`);
          }
        }
      } catch (error) {
        console.error("Error checking existing records:", error);
      }
    };

    checkExistingRecord();

    // Fetch data for edit mode
    if (isEditMode) {
      const fetchMotMinistre = async () => {
        try {
          setIsLoading(true);
          const response = await getMotMinistreById(parseInt(id!));
          const mot = response.data;

          if (mot) {
            // Reset form with all fields
            reset({
              titrearabe: mot.titrearabe || "",
              titrefrancais: mot.titrefrancais || "",
              textarabe: mot.textarabe || "",
              textfrancais: mot.textfrancais || "",
            });

            // Set text editor values
            setArabicText(mot.textarabe || "");
            setFrenchText(mot.textfrancais || "");
          }
        } catch (error) {
          console.error("Error fetching mot du ministre:", error);
          toast.error("حدث خطأ أثناء جلب بيانات كلمة الوزير");
        } finally {
          setIsLoading(false);
        }
      };
      fetchMotMinistre();
    }
  }, [id, isEditMode, reset, navigate]);

  const onSubmit = async (data: MotMinistre) => {
    setIsLoading(true);
    try {
      // Prepare payload EXACTLY as your API expects
      const payload = {
        titrearabe: data.titrearabe,
        titrefrancais: data.titrefrancais,
        textarabe: arabicText,
        textfrancais: frenchText,
      };

      if (isEditMode) {
        await updateMotMinistre(parseInt(id!), payload);
        toast.success("تم تحديث كلمة الوزير بنجاح");
      } else {
        await createMotMinistre(payload);
        toast.success(
          `تمت ${getTranslation("addMinisterWord", language)} بنجاح`,
        );
      }

      navigate("/admin/mot-ministre");
    } catch (error: any) {
      console.error("Error saving mot du ministre:", error);
      const errorMessage =
        error.response?.data?.message ||
        `حدث خطأ أثناء ${getTranslation("save", language)}`;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (hasExistingRecord && !isEditMode) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-6 max-w-md mx-auto">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-yellow-500 text-4xl mb-4"
            />
            <h2 className="text-xl font-bold text-yellow-700 mb-2">تنبيه</h2>
            <p className="text-yellow-600 mb-4">
              يوجد كلمة وزير بالفعل. يسمح بوجود كلمة وزير واحدة فقط في النظام.
            </p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() =>
                  navigate(`/admin/mot-ministre/edit/${existingRecordId}`)
                }
                className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
              >
                تعديل كلمة الوزير الحالية
              </button>
              <button
                onClick={() => navigate("/admin/mot-ministre")}
                className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
              >
                العودة للقائمة
              </button>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>
        {`
          .rtl-quill .ql-editor {
            direction: rtl;
            text-align: right;
          }
          .rtl-quill .ql-editor ol, 
          .rtl-quill .ql-editor ul {
            padding-right: 1.5em;
            padding-left: 0;
          }
          .ltr-quill .ql-editor {
            direction: ltr;
            text-align: left;
          }
          .ltr-quill .ql-editor ol, 
          .ltr-quill .ql-editor ul {
            padding-left: 1.5em;
            padding-right: 0;
          }
        `}
      </style>

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark flex items-center">
          <FontAwesomeIcon icon={faComments} className="ml-2" />
          {isEditMode
            ? "تعديل كلمة الوزير"
            : getTranslation("addMinisterWord", language)}
        </h1>
        <button
          onClick={() => navigate("/admin/mot-ministre")}
          className="bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition duration-300"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="ml-2" />
          {getTranslation("back", language)}
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Titles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="titrearabe">
                العنوان (عربي) *
              </label>
              <input
                id="titrearabe"
                type="text"
                {...register("titrearabe", {
                  required: "هذا الحقل مطلوب",
                  minLength: {
                    value: 2,
                    message: "يجب أن يكون العنوان على الأقل حرفين",
                  },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrearabe ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="مثال: كلمة الوزير"
              />
              {errors.titrearabe && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrearabe.message}
                </p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="titrefrancais"
              >
                العنوان (فرنسي) *
              </label>
              <input
                id="titrefrancais"
                type="text"
                {...register("titrefrancais", {
                  required: "هذا الحقل مطلوب",
                  minLength: {
                    value: 2,
                    message: "يجب أن يكون العنوان على الأقل حرفين",
                  },
                })}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green ${
                  errors.titrefrancais ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="مثال: Mot du Ministre"
              />
              {errors.titrefrancais && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.titrefrancais.message}
                </p>
              )}
            </div>
          </div>

          {/* Text fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 mb-2" htmlFor="textarabe">
                النص (عربي) *
              </label>
              <div className="rtl-quill">
                <ReactQuill
                  theme="snow"
                  value={arabicText}
                  onChange={setArabicText}
                  modules={quillModules}
                  className="h-64 mb-16"
                  placeholder="أدخل نص كلمة الوزير باللغة العربية..."
                />
              </div>
              {!arabicText.trim() && (
                <p className="text-red-500 text-sm mt-1">هذا الحقل مطلوب</p>
              )}
            </div>

            <div>
              <label
                className="block text-gray-700 mb-2"
                htmlFor="textfrancais"
              >
                النص (فرنسي) *
              </label>
              <div className="ltr-quill">
                <ReactQuill
                  theme="snow"
                  value={frenchText}
                  onChange={setFrenchText}
                  modules={quillModules}
                  className="h-64 mb-16"
                  placeholder="Entrez le texte du mot du ministre en français..."
                />
              </div>
              {!frenchText.trim() && (
                <p className="text-red-500 text-sm mt-1">هذا الحقل مطلوب</p>
              )}
            </div>
          </div>

          {/* Preview Section */}
          {(arabicText || frenchText) && (
            <div className="mb-6 p-4 bg-gray-50 rounded-md">
              <h3 className="font-semibold text-gray-700 mb-3">
                {getTranslation("preview", language)} النص:
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {arabicText && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">
                      النص العربي:
                    </h4>
                    <div
                      className="prose prose-sm max-w-none p-3 bg-white rounded border"
                      dangerouslySetInnerHTML={{ __html: arabicText }}
                    />
                  </div>
                )}
                {frenchText && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-2">
                      النص الفرنسي:
                    </h4>
                    <div
                      className="prose prose-sm max-w-none p-3 bg-white rounded border"
                      dangerouslySetInnerHTML={{ __html: frenchText }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading || !arabicText.trim() || !frenchText.trim()}
              className="bg-mauritania-green text-white py-2 px-6 rounded-md hover:bg-mauritania-green-dark transition duration-300 focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:ring-opacity-50 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faSave} className="ml-2" />
              {isLoading
                ? getTranslation("saving", language)
                : getTranslation("save", language)}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default MotMinistreForm;
